import { isNil } from 'lodash';
import { oneDecimalPercentFormatValidateFloatTrueWithNegativeValues, yearsDecimalFormat } from 'common/formats/formats';
import { GridSelect } from 'components/FeaturedSpreadsheet/components';
import SelectValueViewer from 'components/SelectValueViewer/SelectValueViewer';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { ExtendedValuationApproachModel } from 'pages/Valuations/approaches/backsolveApproach/types';
import { CAP_TABLE_SELECTION } from 'pages/Valuations/util/constants';
import { extractGPCApproaches } from 'pages/Valuations/util/util';
import {
  MATURITY_YEARS_CUSTOM_KEY,
  RISK_FREE_RATE_CUSTOM_KEY,
  VOLATILITY_CUSTOM_KEY,
} from 'pages/ValuationsAllocation/approaches/FutureExit/AllocationMethodTable/utils/constants';
import {
  getCellValue,
  getCustomParser,
} from 'pages/ValuationsAllocation/approaches/FutureExit/AllocationMethodTable/utils/utils';
import {
  ALLOCATION_METHOD_OPTIONS,
  ALLOCATION_METHOD_OPTIONS_KEYS,
  AllocationMethodsMapKeys,
  SHEET_ALIASES_CONSTANTS,
  SPECIFIED_LABEL,
} from 'pages/ValuationsAllocation/common/constants/futureExit';
import { FUTURE_EXIT_LABEL } from 'pages/ValuationsAllocation/common/constants/futureExit/futureExit';
import { CapTableVersionSelector } from 'pages/ValuationsAllocation/components';
import { ShowOPMCalculationsValueViewer } from 'pages/ValuationsAllocation/components/ShowOPMCalculationsLink';
import { dbShortDate, getObjectValue, getStringValue, MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';
import { datePlusYears } from 'utilities/dateUtils/dateUtils';
import { CellParserParams, CustomParserParams, MaturityDateProps } from './types';

const {
  FE_ALLOCATION_METHOD_SPREADSHEET_ALLOCATION_METHOD,
  FE_ALLOCATION_METHOD_SPREADSHEET_CAP_TABLE,
  FE_ALLOCATION_METHOD_SPREADSHEET_OPM_INPUTS,
  FE_ALLOCATION_METHOD_SPREADSHEET_MATURITY_YEARS,
  FE_ALLOCATION_METHOD_SPREADSHEET_MATURITY_DATE,
  FE_ALLOCATION_METHOD_SPREADSHEET_RISK_FREE_RATE,
  FE_ALLOCATION_METHOD_SPREADSHEET_VOLATILITY_SOURCE,
  FE_ALLOCATION_METHOD_SPREADSHEET_VOLATILITY,
  FE_FUTURE_EQUITY_SPREADSHEET_FUTURE_VALUE_CURRENT_SHAREHOLDERS_CUSTOM_KEY,
  FE_FUTURE_EQUITY_SPREADSHEET_EXIT_DATE_CUSTOM_KEY,
} = SHEET_ALIASES_CONSTANTS;

const getMaturityDateValue = (props: MaturityDateProps): string => {
  const { column, exitDate } = props;
  if (!exitDate) {
    return '';
  }
  // Check if we have the maturity date value from API
  const { value: defaultMaturityDate } = getObjectValue(column[FE_ALLOCATION_METHOD_SPREADSHEET_MATURITY_DATE]);
  // If we have data, we return it
  if (defaultMaturityDate) {
    return defaultMaturityDate as string;
  }

  // Otherwise... We have to calculate the maturity date
  const { value: maturityYears } = getObjectValue(column[FE_ALLOCATION_METHOD_SPREADSHEET_MATURITY_YEARS]);
  if (isNil(maturityYears) || maturityYears === '') {
    return '';
  }
  const maturityDate = datePlusYears({
    startingDate: dbShortDate(exitDate),
    years: Number(maturityYears),
  })!.format(MOMENT_DEFAULT_DATE_FORMAT);
  return maturityDate;
};

const cellParser = (params: CellParserParams) => {
  const {
    alphabet,
    approachName,
    approaches,
    colIndex,
    column,
    futureEquitySheetName,
    isDisabled,
    row,
    rowIndex,
    sheetName,
    valuationsApproachFutureExit,
  } = params;
  const exitDate = getStringValue(valuationsApproachFutureExit?.exit_date);
  const publicCompsList = [
    { value: null, label: SPECIFIED_LABEL },
    ...(extractGPCApproaches({ approaches: approaches as unknown as ExtendedValuationApproachModel[] }) ?? []),
  ];

  const ROW_NUMBER = rowIndex + 1;
  const columnLegend = alphabet[colIndex];
  const cellKey = columnLegend + ROW_NUMBER;

  const { value: columnValue } = getObjectValue(column[row.alias]);
  const { value: volatilitySource } = column[FE_ALLOCATION_METHOD_SPREADSHEET_VOLATILITY_SOURCE];

  // Creating Cell from Row and Column
  const cell = { ...row, key: cellKey, value: columnValue };

  // Parse Cell based on Row alias
  switch (row.alias) {
    case FE_ALLOCATION_METHOD_SPREADSHEET_ALLOCATION_METHOD:
      cell.value =
        typeof columnValue === 'number'
          ? ALLOCATION_METHOD_OPTIONS_KEYS[columnValue as keyof AllocationMethodsMapKeys]
          : columnValue;
      cell.dbType = 'number';
      cell.gridType = 'string';
      cell.valueViewer = (props: any) => (
        <SelectValueViewer options={Object.values(ALLOCATION_METHOD_OPTIONS)} useLabel {...props} />
      );
      cell.dataEditor = (props: any) => (
        <GridSelect
          enumerated
          options={Object.values(ALLOCATION_METHOD_OPTIONS)}
          optionsTextAlign="left"
          useHandleChange={false}
          {...props}
        />
      );
      cell.forceComponent = true;
      break;

    case FE_ALLOCATION_METHOD_SPREADSHEET_CAP_TABLE:
      cell.value = columnValue;
      cell.isRequired = true;
      cell.dbType = 'number';
      cell.customKey = CAP_TABLE_SELECTION;
      cell.gridType = 'number';
      cell.dataEditor = props => <CapTableVersionSelector {...props} />;
      cell.valueViewer = props => <SelectValueViewer {...props} />;
      cell.forceComponent = true;
      break;

    case FE_ALLOCATION_METHOD_SPREADSHEET_OPM_INPUTS:
      cell.value = columnValue;
      cell.hasSubRows = true;
      cell.showSubRows = true;
      cell.hidden = true;
      // The grouping row's only data cell is blank, which leaves room for the link to the OPM
      // Calculator. Addressed by customKey: this sheet has a single column, so each key is unique.
      if (sheetName) {
        cell.opmCalculatorLink = {
          label: approachName || FUTURE_EXIT_LABEL,
          sheetName,
          cells: {
            capTableId: { key: CAP_TABLE_SELECTION },
            equityValue: futureEquitySheetName
              ? {
                sheetName: futureEquitySheetName,
                key: FE_FUTURE_EQUITY_SPREADSHEET_FUTURE_VALUE_CURRENT_SHAREHOLDERS_CUSTOM_KEY,
              }
              : undefined,
            maturity: { key: MATURITY_YEARS_CUSTOM_KEY },
            riskFreeRate: { key: RISK_FREE_RATE_CUSTOM_KEY },
            volatility: { key: VOLATILITY_CUSTOM_KEY },
          },
          // Future Exit prices OPM at the exit date: dividends accrue to exit date + maturity,
          // as in the backend allocation. Read off the Future Equity sheet so an unsaved exit date
          // edit counts; the saved one covers a sheet that has not evaluated yet.
          opmDate: exitDate || undefined,
          opmDateCell: futureEquitySheetName
            ? { sheetName: futureEquitySheetName, key: FE_FUTURE_EQUITY_SPREADSHEET_EXIT_DATE_CUSTOM_KEY }
            : undefined,
        };
        cell.valueViewer = ShowOPMCalculationsValueViewer;
      }
      break;

    case FE_ALLOCATION_METHOD_SPREADSHEET_MATURITY_YEARS:
      cell.value = columnValue;
      cell.customKey = MATURITY_YEARS_CUSTOM_KEY;
      cell.gridType = 'number';
      cell.dbType = 'string';
      cell.format = {
        ...yearsDecimalFormat,
        maximumFractionDigits: 2,
        maximumFractionDigitsWhenEditing: 4,
      };
      break;

    case FE_ALLOCATION_METHOD_SPREADSHEET_MATURITY_DATE:
      cell.value = getMaturityDateValue({ column, exitDate });
      cell.gridType = 'gridDate';
      break;

    case FE_ALLOCATION_METHOD_SPREADSHEET_RISK_FREE_RATE:
      cell.value = columnValue;
      cell.gridType = 'percentage';
      cell.customKey = RISK_FREE_RATE_CUSTOM_KEY;
      cell.dbDecimalPlaces = 4;
      cell.format = oneDecimalPercentFormatValidateFloatTrueWithNegativeValues;
      break;

    case FE_ALLOCATION_METHOD_SPREADSHEET_VOLATILITY_SOURCE:
      cell.value = columnValue;
      cell.gridType = 'number';
      cell.dbType = 'number';
      cell.valueViewer = (props: any) => <SelectValueViewer options={publicCompsList} {...props} />;
      cell.dataEditor = (props: any) => <GridSelect menuPosition="absolute" options={publicCompsList} {...props} />;
      break;

    case FE_ALLOCATION_METHOD_SPREADSHEET_VOLATILITY:
      cell.value = columnValue || 0;
      cell.gridType = 'percentage';
      cell.dbType = 'string';
      cell.customKey = VOLATILITY_CUSTOM_KEY;
      cell.dbDecimalPlaces = 4;
      cell.readOnly = !isNil(volatilitySource) || isDisabled;
      cell.format = oneDecimalPercentFormatValidateFloatTrueWithNegativeValues;
      break;

    default:
      break;
  }

  const cellData = getCellValue(cell, columnLegend);

  return {
    [cell.key]: {
      ...cellData,
    } as ICell,
  };
};

// The shared `getCustomParser` only forwards the params the Waterfall/CSE table also needs, so
// what the OPM Calculator link uses is injected here rather than widened into it.
const customParser = (params: CustomParserParams) =>
  getCustomParser({
    parserParams: params,
    cellParser: (cellParserParams: CellParserParams) =>
      cellParser({
        ...cellParserParams,
        approachName: params.approachName,
        futureEquitySheetName: params.futureEquitySheetName,
        sheetName: params.sheetName,
      }),
  });

export default customParser;
